// One cost & financing engine for every path that buys or sells a home:
// buying power, the Sell → Buy planner, the upgrade window and the dashboard.
// Pure functions, browser-only. Policy numbers come from ../policy/policy.ts
// (dated, cited); app assumptions live in COST_ASSUMPTIONS below and are listed
// in DESIGN.md's assumptions register.
//
// Money is always split into CASH and CPF OA. They are not interchangeable:
// the minimum cash downpayment and (conservatively) ABSD need cash, and a sale
// refunds CPF used (+ accrued interest) to the OA, not to your bank account.

import { absdRate, at, bsd, CPF_HOUSING, HDB_LOAN_RATE, HDB_LOAN_TERMS, LOANS, ssdRate, today, yearsBefore, type Residency } from "../policy/policy";

// ---- App assumptions (not policy) -------------------------------------------
export const COST_ASSUMPTIONS = {
  sellerAgentRate: 0.02, // owner: seller's agent commission when using an agent
  buyerAgentRate: 0.01, // owner: buyer's agent fee when using one (DIY playbook's "~1% avoided")
  legalFee: { hdb: 1_800, private: 2_800 }, // owner: conveyancing, per the DIY playbook estimates
  soraSpread: 0.005, // owner: bank package spread over 3M SORA when no rate is entered
  bankMaxAge: 75, // Claude: typical bank lending age cap (practice, not MAS policy — UNVERIFIED)
};

// ---- Buyer / ABSD -----------------------------------------------------------
export interface Buyer {
  residency: Residency;
  married: boolean;
  spouseResidency?: Residency;
  age: number; // youngest borrower
}

export interface AbsdResult {
  rate: number; // rate actually paid upfront
  amount: number;
  refundable: boolean; // refunded if the first home is sold within the window
  note: string;
}

/**
 * ABSD for buying the buyer's `nth` residential property. A married couple is
 * assumed to buy jointly, so the higher of the two profiles applies (IRAS). With
 * an SC spouse: the first home is fully remitted; a second is paid upfront and
 * refundable if the first is sold in time (`sellingFirst`).
 */
export function absdFor(price: number, b: Buyer, nth: number, sellingFirst: boolean, date?: string): AbsdResult {
  const own = absdRate(b.residency, nth, date);
  const joint = b.married && b.spouseResidency ? Math.max(own, absdRate(b.spouseResidency, nth, date)) : own;
  const scCouple = b.married && (b.residency === "SC" || b.spouseResidency === "SC");
  if (scCouple && nth <= 1 && joint > 0) {
    return { rate: 0, amount: 0, refundable: false, note: "Married couple with an SC spouse buying their first home jointly: full ABSD remission (IRAS)." };
  }
  if (scCouple && nth === 2 && sellingFirst && joint > 0) {
    return { rate: joint, amount: joint * price, refundable: true, note: "Paid upfront; refunded if the first home is sold within 6 months of buying a completed second home (IRAS spouses remission)." };
  }
  return { rate: joint, amount: joint * price, refundable: false, note: joint > 0 ? "Not reclaimable in this scenario." : "No ABSD." };
}

// ---- Loan terms -------------------------------------------------------------
export type Lender = "hdb" | "bank";

export interface LoanTerms {
  lender: Lender;
  label: string;
  ltv: number;
  minCashPct: number; // of price, must be cash (not CPF)
  stressRate: number;
  tenureYears: number;
  marketRate: number; // estimated actual rate
  useMsr: boolean; // MSR 30% applies (HDB flats / ECs)
  useTdsr: boolean; // TDSR 55% applies (FI loans)
  eligible: boolean; // false = shown for comparison, not counted as buying power
  note: string;
}

export interface LoanContext {
  isHdbFlat: boolean;
  age: number;
  outstandingHousingLoans: number; // housing loans still running when this one starts
  bankRate: number; // estimated bank package rate (decimal)
  leaseLtvFactor?: number; // HDB short-lease pro-ration (lease-decay engine)
  leaseTenureCap?: number;
  remainingLease?: number; // HDB flat's remaining lease (HDB loan: tenure ≤ lease − 20)
  householdIncome?: number; // for the HDB loan income ceiling
  hasCitizen?: boolean; // at least one applicant is an SC (HDB loan)
  date?: string;
}

/** Every loan the buyer could take for this purchase, per MAS/HDB rules. */
export function loanOptions(c: LoanContext): LoanTerms[] {
  const L = at(LOANS, c.date).value;
  const leaseLtv = c.leaseLtvFactor ?? 1;
  const leaseCap = c.leaseTenureCap ?? Infinity;
  const out: LoanTerms[] = [];
  const masMax = c.isHdbFlat ? L.maxTenureHdb : L.maxTenureOther;
  const tierTenure = c.isHdbFlat ? L.ltvTierTenureHdb : L.ltvTierTenureOther;
  const capTenure = (t: number) => Math.floor(Math.min(t, leaseCap));

  // Bank loan — full tier: tenure within the tier limit and repaid by age 65.
  const second = c.outstandingHousingLoans >= 1;
  const fullT = capTenure(Math.min(tierTenure, L.ltvTierAge - c.age));
  if (fullT >= 5) {
    out.push({
      lender: "bank", label: second ? "Bank loan (2nd loan, 45%)" : "Bank loan (75% tier)",
      ltv: (second ? L.bankLtvSecond : L.bankLtvFirst) * leaseLtv,
      minCashPct: second ? L.minCashDownSecond : L.minCashDownFirst,
      stressRate: L.bankStressRate, tenureYears: fullT, marketRate: c.bankRate,
      useMsr: c.isHdbFlat, useTdsr: true, eligible: true,
      note: `Tenure ${fullT} yrs keeps the higher LTV tier (≤ ${tierTenure} yrs and repaid by ${L.ltvTierAge}).`,
    });
  }
  // Bank loan — lower tier: longer tenure / past 65 → 55% (or 25% on a 2nd loan).
  const longT = capTenure(Math.min(masMax, COST_ASSUMPTIONS.bankMaxAge - c.age));
  if (longT > fullT && longT >= 5) {
    out.push({
      lender: "bank", label: second ? "Bank loan (2nd loan, 25%, longer tenure)" : "Bank loan (55% tier, longer tenure)",
      ltv: (second ? L.bankLtvSecondReduced : L.bankLtvFirstReduced) * leaseLtv,
      minCashPct: second ? L.minCashDownSecond : L.minCashDownFirstReduced,
      stressRate: L.bankStressRate, tenureYears: longT, marketRate: c.bankRate,
      useMsr: c.isHdbFlat, useTdsr: true, eligible: true,
      note: `Tenure ${longT} yrs drops to the lower LTV tier (lower monthly, more cash upfront).`,
    });
  }
  // HDB concessionary loan — HDB flats only; MSR applies, no minimum cash.
  // Tenure: shortest of 25 yrs, 65 − age, remaining lease − 20 (HDB).
  if (c.isHdbFlat && !second) {
    const H = at(HDB_LOAN_TERMS, c.date).value;
    const leaseT = c.remainingLease != null ? c.remainingLease - H.leaseBuffer : Infinity;
    const hdbT = capTenure(Math.min(H.maxTenure, H.repayByAge - c.age, leaseT));
    const reasons: string[] = [];
    if (H.requiresCitizen && c.hasCitizen === false) reasons.push("needs at least one Singapore Citizen applicant");
    if (c.householdIncome != null && c.householdIncome > H.incomeCeilingFamily) reasons.push(`household income above the S$${H.incomeCeilingFamily.toLocaleString()} ceiling`);
    if (hdbT < 5) reasons.push("repayment period too short (age / remaining lease)");
    out.push({
      lender: "hdb", label: "HDB loan",
      ltv: L.hdbLoanLtv * leaseLtv, minCashPct: 0,
      stressRate: L.hdbStressRate, tenureYears: Math.max(hdbT, 1), marketRate: at(HDB_LOAN_RATE, c.date).value,
      useMsr: true, useTdsr: false, eligible: reasons.length === 0,
      note: reasons.length ? `Not eligible: ${reasons.join("; ")}.` : "HDB concessionary loan — subject to HDB's credit assessment (HFE letter).",
    });
  }
  return out;
}

// ---- Annuity helpers --------------------------------------------------------
export function annuityPV(monthly: number, annualRate: number, years: number): number {
  const r = annualRate / 12;
  const n = years * 12;
  if (monthly <= 0 || n <= 0) return 0;
  if (r === 0) return monthly * n;
  return (monthly * (1 - Math.pow(1 + r, -n))) / r;
}
export function annuityPayment(principal: number, annualRate: number, years: number): number {
  const r = annualRate / 12;
  const n = years * 12;
  if (principal <= 0 || n <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

// ---- Funding a purchase -----------------------------------------------------
export interface Funds {
  cash: number; // cash + liquid assets (+ cash proceeds from a sale)
  cpf: number; // CPF OA (+ CPF refunded from a sale)
}

export interface Obligations {
  income: number; // gross monthly
  otherDebt: number; // non-housing monthly repayments (TDSR)
  housingDebt: number; // existing housing-loan monthly repayments (TDSR + MSR)
}

export interface PurchaseInput {
  price: number;
  isHdbFlat: boolean;
  buyer: Buyer;
  nth: number; // which residential property this is for the buyer
  sellingFirst: boolean; // the current home is being sold (ABSD refund path)
  useAgent: boolean;
  cpfLeaseFraction?: number; // short-lease CPF pro-ration (fraction of price)
  date?: string;
}

export interface Funding {
  terms: LoanTerms;
  price: number;
  loan: number;
  loanCap: number; // max loan by income at the stress rate
  boundedBy: "income" | "ltv";
  downpayment: number;
  bsd: number;
  absd: AbsdResult;
  legal: number;
  agentFee: number;
  cashNeeded: number;
  cpfNeeded: number;
  shortfall: number; // > 0 = funding gap
  monthlyStress: number;
  monthlyMarket: number;
}

/** How a purchase at `price` would be funded under `terms`. */
export function fundPurchase(inp: PurchaseInput, terms: LoanTerms, funds: Funds, ob: Obligations): Funding {
  const L = at(LOANS, inp.date).value;
  const cpfRules = at(CPF_HOUSING, inp.date).value;
  const P = inp.price;
  const tdsrRoom = Math.max(0, L.tdsr * ob.income - ob.otherDebt - ob.housingDebt);
  const msrRoom = Math.max(0, L.msr * ob.income - ob.housingDebt);
  const room = Math.min(terms.useTdsr ? tdsrRoom : Infinity, terms.useMsr ? msrRoom : Infinity);
  const loanCap = annuityPV(Number.isFinite(room) ? room : 0, terms.stressRate, terms.tenureYears);
  const loan = Math.max(0, Math.min(loanCap, terms.ltv * P));
  const boundedBy = loanCap < terms.ltv * P ? "income" : "ltv";
  const downpayment = P - loan;

  const duty = bsd(P, inp.date);
  const absd = absdFor(P, inp.buyer, inp.nth, inp.sellingFirst, inp.date);
  const legal = inp.isHdbFlat ? COST_ASSUMPTIONS.legalFee.hdb : COST_ASSUMPTIONS.legalFee.private;
  const agentFee = inp.useAgent ? COST_ASSUMPTIONS.buyerAgentRate * P : 0;

  // CPF: may fund the downpayment beyond the minimum cash, up to the (lease
  // pro-rated) limit on the price; plus BSD and legal fees in full. ABSD and
  // agent fees are cash.
  const cashMin = terms.minCashPct * P;
  const priceLimit = (inp.cpfLeaseFraction ?? 1) * P;
  const cpfForPrice = Math.min(funds.cpf, Math.max(0, downpayment - cashMin), priceLimit);
  const cpfLeft = funds.cpf - cpfForPrice;
  const cpfFees = cpfRules.dutiesAndLegalFromCpf ? Math.min(cpfLeft, duty + legal + (cpfRules.absdFromCpf ? absd.amount : 0)) : 0;
  const cpfNeeded = cpfForPrice + cpfFees;
  const cashNeeded = downpayment - cpfForPrice + duty + absd.amount + legal + agentFee - cpfFees;
  const shortfall = Math.max(0, cashNeeded - funds.cash);

  return {
    terms, price: P, loan, loanCap, boundedBy, downpayment, bsd: duty, absd, legal, agentFee,
    cashNeeded, cpfNeeded, shortfall,
    monthlyStress: annuityPayment(loan, terms.stressRate, terms.tenureYears),
    monthlyMarket: annuityPayment(loan, terms.marketRate, terms.tenureYears),
  };
}

/** Highest price fundable under `terms` (bisection; needs rise with price). */
export function maxFundable(inp: Omit<PurchaseInput, "price">, terms: LoanTerms, funds: Funds, ob: Obligations): Funding {
  const ok = (P: number) => fundPurchase({ ...inp, price: P }, terms, funds, ob).shortfall <= 0;
  let hi = 1_000_000;
  while (ok(hi) && hi < 1e10) hi *= 2;
  let lo = 0;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (ok(mid)) lo = mid;
    else hi = mid;
  }
  return fundPurchase({ ...inp, price: lo }, terms, funds, ob);
}

// ---- Selling ----------------------------------------------------------------
export interface Holding {
  value: number;
  outstandingLoan: number;
  yearsHeld: number;
  /** False when the user hasn't told us how long they've held it. SSD and the
   *  instalment part of the CPF refund are then NOT computed (and flagged),
   *  rather than computed as if bought today. Omitted = known (back-compat). */
  yearsHeldKnown?: boolean;
  isHdbFlat: boolean;
  cpfRefundOverride?: number; // exact P + I from the CPF dashboard, if known
  cpfLumpSum?: number; // CPF used at purchase (downpayment, duties, legal)
  cpfMonthly?: number; // monthly CPF instalment towards the loan
}

/** Accrued interest on CPF used: what it would have earned left in the OA. */
export function accruedInterest(lump: number, monthly: number, years: number, oaRate: number): number {
  const r = oaRate / 12;
  const n = Math.max(0, Math.round(years * 12));
  if (n === 0) return 0;
  const g = Math.pow(1 + r, n);
  const onLump = lump * (g - 1);
  const onMonthly = r > 0 ? monthly * ((g - 1) / r - n) : 0;
  return onLump + onMonthly;
}

export interface SaleResult {
  value: number;
  ssd: number;
  ssdRate: number;
  agentFee: number;
  legal: number;
  outstandingLoan: number;
  netProceeds: number; // after loan, SSD and selling costs
  cpfPrincipal: number;
  cpfAccruedInterest: number;
  cpfRefundDue: number; // P + I
  cpfRefund: number; // actually refunded (capped at proceeds for a market-value sale)
  cash: number; // to your bank account
  purchaseDate: string;
  estimatedRefund: boolean; // P + I computed, not taken from the CPF dashboard
  /** Holding period unknown: SSD excluded from the figures above. */
  ssdUnknown: boolean;
  /** SSD if the property were sold inside the first year — the most it could be. */
  ssdIfRecent: number;
  /** Holding period unknown, so CPF from monthly instalments isn't in the refund. */
  cpfRefundIncomplete: boolean;
}

export function saleProceeds(h: Holding, useAgent: boolean, date: string = today()): SaleResult {
  const known = h.yearsHeldKnown !== false;
  const purchaseDate = yearsBefore(h.yearsHeld, date);
  const rate = h.value > 0 && known ? ssdRate(purchaseDate, h.yearsHeld) : 0;
  const ssdIfRecent = h.value > 0 ? h.value * ssdRate(yearsBefore(0, date), 0) : 0;
  const ssd = h.value * rate;
  const agentFee = useAgent ? COST_ASSUMPTIONS.sellerAgentRate * h.value : 0;
  const legal = h.value > 0 ? (h.isHdbFlat ? COST_ASSUMPTIONS.legalFee.hdb : COST_ASSUMPTIONS.legalFee.private) : 0;
  const netProceeds = Math.max(0, h.value - h.outstandingLoan - ssd - agentFee - legal);

  const oaRate = at(CPF_HOUSING, date).value.oaRate;
  const lump = Math.max(0, h.cpfLumpSum ?? 0);
  const monthly = Math.max(0, h.cpfMonthly ?? 0);
  // Unknown holding period: count only the lump sum (no instalments, no
  // interest) and flag it, instead of pretending 0 months of instalments.
  const principal = lump + (known ? monthly * Math.round(h.yearsHeld * 12) : 0);
  const interest = known ? accruedInterest(lump, monthly, h.yearsHeld, oaRate) : 0;
  const override = (h.cpfRefundOverride ?? 0) > 0;
  const due = override ? (h.cpfRefundOverride as number) : principal + interest;
  const cpfRefund = Math.min(due, netProceeds);
  return {
    value: h.value, ssd, ssdRate: rate, agentFee, legal, outstandingLoan: h.outstandingLoan, netProceeds,
    cpfPrincipal: override ? NaN : principal, cpfAccruedInterest: override ? NaN : interest,
    cpfRefundDue: due, cpfRefund, cash: netProceeds - cpfRefund, purchaseDate, estimatedRefund: !override,
    ssdUnknown: !known && h.value > 0, ssdIfRecent,
    cpfRefundIncomplete: !known && !override && (lump > 0 || monthly > 0),
  };
}

/** Bank package rate: the user's own rate, else live 3M SORA + spread. */
export function bankRate(userRatePct: number | undefined, manual: boolean, sora: number | null | undefined): { rate: number; source: "manual" | "sora" | "default" } {
  if (manual && userRatePct != null) return { rate: userRatePct / 100, source: "manual" };
  if (sora != null && Number.isFinite(sora)) return { rate: sora / 100 + COST_ASSUMPTIONS.soraSpread, source: "sora" };
  return { rate: (userRatePct ?? 3.5) / 100, source: "default" };
}
